import { addDays, format, isValid, parse } from 'date-fns';
import httpHelper from 'helpers/httpHelper';

export default {
  getCurrentUser,
  getUsers,
  getApiKeys,
  createApiKey,
  searchTravelAdvices,
  searchTravelAdvicesLevel,
  searchStatistics,
  getAccountDetails,
  getOwnAccountDetails,
  sendContactMessage,
  getAdminData,
  getPlans,
  saveItinerary,
  getItineraries,
  deleteItinerary
};

export class TripEntry {
  from = '';
  to = '';
  departure = '';
  index = 0;
  last = false;
}

export class Itinerary {
  id = '';
  user_id = '';
  name = '';
  start_date = '';
  end_date = '';
  status = '';
  email_recipient = '';
  sms_recipient = '';
  webhook_recipient = '';
  trips = [] as Trip[];
}

export class Trip {
  id = '';
  itinerary_id = '';
  location_id = '';
  start_date = '';
  from = '';
  to = '';
  from_airport = '';
  to_airport = '';
  country_code = '';
  risk_level = 0;
  requirements = {} as Requirements;
  restrictions = {} as Restrictions;
  total_cases = 0;
  new_cases = 0;
  total_deaths = 0;
  new_deaths = 0;
  government_response_index = '';
  stringency_index = '';
  containment_and_health_index = '';
  economic_support_index = '';
}

export class Requirements {
  tests = '';
  quarantine = '';
  masks = '';
}

export class Restrictions {
  school_closing = 1;
  workplace_closing = 2;
  cancel_public_events = 2;
  restrictions_on_gatherings = 4;
  close_public_transport = 1;
  stay_at_home_requirements = 2;
  restrictions_on_internal_movement = 1;
  international_travel_controls = 2;
  testing_policy = 2;
  contact_tracing = 1;
}

export class SearchQuery {
  return_date = '';
  ignore_airports = false;
  trips: TripEntry[] = [];
}

function getCurrentUser() {
  return httpHelper.get('/me', {}, {notVersioned: true, acceptableErrorCodes: [401]});
}

async function getUsers() {
  return await httpHelper.get('/users');
}

async function getApiKeys() {
  return await httpHelper.get('/keys');
}

async function createApiKey() {
  return await httpHelper.post('/keys', {});
}

async function searchTravelAdvices(search: TripEntry[]) {
  if (search.length <= 0) {
    return;
  }
  
  let lastDeparture = parse(search[search.length - 1].departure, 'yyyy-MM-dd', new Date());
  
  if (!isValid(lastDeparture)) {
    console.log("invalid date provided in search query", search[search.length - 1].departure, lastDeparture);
    return;
  }
  
  let return_date = addDays(lastDeparture, 30);

  let query: SearchQuery = {
    return_date: format(return_date, 'yyyy-MM-dd'),
    ignore_airports: true,
    trips: search
  };

  return await httpHelper.post(`/search`, query, {api_key: true, notVersioned: true});
}

async function saveItinerary(id: string) {
  if (id === '') {
    return;
  }
  
  return await httpHelper.post(`/trips`, {id: id}, {});
}

async function searchTravelAdvicesLevel(level) {
  return await httpHelper.get(`/search/level/${level}`);
}

async function searchStatistics(countryCode, limit) {
  let options = {limit};

  return await httpHelper.get(`/search/stats/${countryCode}`, options, {api_key: true, notVersioned: true});
}

async function getAccountDetails(userId) {
  return await httpHelper.get(`/users/${userId}/account`);
}

async function getOwnAccountDetails() {
  return await httpHelper.get(`/users/account`);
}

interface ContactMessage {
  name: string;
  email: string;
  honeypot: string;
  message: string;
}

async function sendContactMessage(contactMessage: ContactMessage) {
  return await httpHelper.post(
    `/contact`,
    {
      Name: contactMessage.name,
      Email: contactMessage.email,
      Honeypot: contactMessage.honeypot,
      Subject: 'Contact message',
      Message: contactMessage.message
    },
    {notVersioned: true}
  );
}

async function getAdminData() {
  return await httpHelper.get(`/users/summary`);
}

async function getPlans() {
  return await httpHelper.get('/plans', {}, {notVersioned: true});
}

async function getItineraries() {
  return await httpHelper.get('/trips', {}, {});
}

async function deleteItinerary(id: string) {
  return await httpHelper.delete('/trips/'+id, {});
}
